import { Controller } from "stimulus";
import humps from 'humps';
import tippy from "tippy.js";
import { Portuguese } from "flatpickr/dist/l10n/pt.js"
import 'tippy.js/themes/light.css';
import '../styles/partials/_card-menu.scss';
import AjaxLoader from "../entrypoints/src/admin/utilities/ajax-loader";
import Swal from "sweetalert2";
import AttendanceService from "../entrypoints/src/admin/services/attendance-service";

export default class extends Controller {
  initializeBase() {
    AjaxLoader.hide();
    this.initDatePickerInline();
    this.adjustCalendar();

    $(this.element.querySelectorAll('.submit-form')).on('change', (ev) => this.submitForm(ev));
    this.element.querySelectorAll('.toggle-filter').forEach(($el) => {
      if (!$el.dataset.listeningEvent)
        $el.addEventListener('click', (ev) => { this.element.querySelector('.filters').classList.toggle('in') } )
      $el.dataset.listeningEvent = true;
    });

    this.element.querySelectorAll('a.date').forEach(($a) => {
      $a.addEventListener('click', (ev) => {
        ev.stopPropagation();
        ev.preventDefault();
        this.inputDateTarget.value = $a.dataset.date;
        this.submitForm();
      });
    });

    this.element.querySelectorAll('a.view-mode-toggle').forEach(($a) => {
      $a.addEventListener('click', (ev) => {
        ev.stopPropagation();
        ev.preventDefault();
        if (!this.hasInputViewModeTarget) return;
        this.inputViewModeTarget.value = $a.dataset.viewMode;
        this.submitForm();
      });
    });
  }

  initializeCardProperties() {
    this.element.querySelectorAll('.card').forEach((card) => {
      window.matchMedia("(max-width: 576px)").matches ? this.initializeModalCardDetails(card) : this.initializeTippyCardDetails(card);
    });

    $('body').on('click', '.c-card a.c-card__menu-item', (ev) => {
      let $a = ev.currentTarget,
        tippyRoot = $a.closest('[data-tippy-root]');

      // A contract part changes what the card shows: fetch the card again on its next opening.
      if ($a.dataset.calendarCardStale) {
        if (tippyRoot) tippyRoot._tippy._initialized = false;
        else if (this.modalCard) this.modalCard._modal = null;
      }

      !!tippyRoot && tippyRoot._tippy.hide();
      ($a.classList.contains('close-mask') || this.element.querySelectorAll('.card.open').length > 0) && document.querySelector('.schedule-mask').click();
    });

    $('body').on('click', '.c-card a.c-card__menu-item.company-answer', (ev) => {
      ev.preventDefault();
      this.updateCompanyAnswer(ev.currentTarget)
    });
  }

  initializeModalCardDetails(card) {
    card.addEventListener('click', () => {
      // Remove any leftover modal from a previous interaction before inserting a new one
      document.querySelector('.modal#card-modal-detail')?.remove();

      let schedule = humps.camelizeKeys(JSON.parse(card.dataset.schedule));
      this.modalCard = card;
      this.modalCardDetails(card, schedule).then((resp) => {
        document.body.insertAdjacentHTML('beforeend', resp);
        const $modal = $(`.modal#card-modal-detail`);
        $modal.modal('show');
        // Use .one() so the handler fires exactly once and never accumulates
        $modal.one('hidden.bs.modal', () => {
          document.querySelector('.schedule-mask')?.click();
          $modal[0]?.remove();
        });
      });
    });
  }

  async modalCardDetails(card, schedule) {
    if (card._modal)
      return card._modal;

    return card._modal = `
      <div class="modal fade" data-keyboard='true' data-backdrop="false" id="card-modal-detail" tabindex="-1" role="dialog">
        <div class="modal-dialog modal-fullscreen" role="document">
          <div class="modal-content">
            ${await this.fetchCardDetails(card, schedule)}
          </div>
        </div>
      </div>
    `;
  }

  initializeTippyCardDetails(card) {
    let _self = this;
    tippy(card, {
      content: 'Carregando...',
      trigger: 'click', // mouseenter, click, focus; manual.
      placement: 'bottom',
      appendTo: document.body,
      arrow: true,
      allowHTML: true,
      theme: 'light',
      animation: 'scale',
      duration: [250, 200],
      maxWidth: '470px',
      interactive: true,
      // Above the opened board card (10005), below the admin nav bar (10100).
      zIndex: 10050,
      // When neither side of the card fits the popover, let it slide over the card instead of leaving the viewport.
      popperOptions: { modifiers: [{ name: 'preventOverflow', options: { altAxis: true, tether: false, padding: { top: this.viewportTopInset() + 12, bottom: 12, left: 12, right: 12 } } }] },
      delay: [165, 200],
      onShown: async (instance) => {
        if (!!instance._initialized)
          return _self.fitCardToViewport(instance);

        _self.selectedCard = card;
        let schedule = humps.camelizeKeys(JSON.parse(card.dataset.schedule)),
          response = await _self.fetchCardDetails(card, schedule);

        instance.setContent(response);
        instance._initialized = true;
        instance._card = card;
        _self.fitCardToViewport(instance);
      }
    });
  }

  // Caps the scrollable region of the popover ([data-calendar-card-scroll]) so the whole card fits the
  // viewport on the side it opens to: the larger free space above or below the clicked card.
  fitCardToViewport(instance) {
    const region = instance.popper.querySelector('[data-calendar-card-scroll]');
    if (!region)
      return;

    region.style.maxHeight = '';
    const viewportMargin = 24,
      minRegionHeight = 240, // about five students
      topInset = this.viewportTopInset(),
      visibleHeight = window.innerHeight - topInset,
      reference = instance.reference.getBoundingClientRect(),
      sideSpace = Math.min(Math.max(window.innerHeight - reference.bottom, reference.top - topInset), visibleHeight) - viewportMargin,
      fixedPartsHeight = instance.popper.getBoundingClientRect().height - region.getBoundingClientRect().height;
    let regionHeight = sideSpace - fixedPartsHeight;

    // Neither side fits: use the whole visible height (preventOverflow slides the popover over the card).
    if (regionHeight < minRegionHeight)
      regionHeight = visibleHeight - (2 * viewportMargin) - fixedPartsHeight;

    region.style.maxHeight = `${Math.max(regionHeight, minRegionHeight)}px`;
    instance.popperInstance?.update();
  }

  // Height covered by the fixed admin nav bar at the top of the viewport.
  viewportTopInset() {
    return Math.max(document.querySelector('.nav-bar-admin')?.getBoundingClientRect().bottom || 0, 0);
  }

  async fetchCardDetails(card, schedule) {
    if (!this.calendarService)
      this.calendarService = await this.loadCalendarService();

    return !!schedule.batchScheduleId
      ? await this.calendarService.batchScheduleCard(schedule.batchScheduleId, card.dataset.currentDate, schedule.startTime.replace(/\D/gi, '').substring(0, 4), schedule.endTime.replace(/\D/gi, '').substring(0, 4))
        : await this.calendarService.attendanceCard(schedule.serviceId, card.dataset.currentDate, schedule.startTime.replace(/\D/gi, '').substring(0, 4), schedule.endTime.replace(/\D/gi, '').substring(0, 4), schedule.enrollmentContractId);
  }

  loadCalendarService() {
    let service = this.calendarServiceValue;
    return new Promise((resolve, reject) => {
      import(/* webpackMode: "eager" */ `../entrypoints/src/admin/services/${service || 'calendar-service'}`)
        .then(({ default: service }) => resolve(service))
        .catch((err) => reject(err))
    })
  }

  initDatePickerInline() {
    let $dateAttendance = this.element.querySelector('input.input-date-attendance');
    flatpickr($dateAttendance, {
      positionElement: $dateAttendance.closest('.flatpickr-open'),
      locale: Portuguese,
      onChange: (selectedDates) => {
        this.inputDateTarget.value = flatpickr.formatDate(selectedDates[0], 'Y-m-d');
        this.submitForm();
      }
    });

    $('.flatpickr-open').on('click', () => $dateAttendance._flatpickr.open());
  }

  updateCompanyAnswer(link) {
    Swal.fire({
      title: 'Atenção',
      html: `Deseja realmente <b>${link.dataset.askText}</b>?`,
      icon: 'warning',
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Sim, tenho certeza!'
    }).then(async (result) => {
      if (!result.value) return;

      try {
        let response = await AttendanceService[link.dataset.companyAnswer === 'yes' ? 'confirm' : 'reject'](link.dataset.attendanceInteractionId);

        if (!!response)
          return Swal.fire(`Aula ${link.dataset.responseText}`, 'A página será atualizada', 'success').then(() => window.location.reload());
      } catch(ex) {}

      Swal.fire('Encontramos um erro', 'Atualize a página e tente novamente', 'error');
    });
  }

  formatDate(americanDate) {
    let year, month, day;
    ({year, month, day} = americanDate.match(/^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})$/i).groups);

    return `${day}/${month}/${year}`;
  }


  submitForm() {
    AjaxLoader.show();
    this.formTarget.insertAdjacentHTML('afterBegin', '<button id="submit-button" class="d-none"></button>');
    this.formTarget.querySelector('#submit-button').click();
  }

  adjustCalendar() {
    if (window.matchMedia("(max-width: 576px)").matches) {
      return;
    }

    let panelScroll = this.element.querySelector('.filters');

    panelScroll.style.height = `calc(100vh - ${panelScroll.getBoundingClientRect().y + 10}px)`;

    $(panelScroll).on('scroll', (ev) => {
      panelScroll.closest('.side-panel-container').classList.toggle('scrolled', panelScroll.scrollTop > 0);
    });
  }
}